import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_CHANGE_FAILED_MESSAGE = "Couldn't update your cart. Please try again.";

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Quantity updates that are still in flight.
   * @returns {Promise<void>[]}
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #waitingToSubmit = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * Re-renders the cart when the page comes back through Back or Forward.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, and a Back there rebuilds the document from the HTTP disk cache
   * instead: `persisted` is false and the frozen cart markup renders anyway. Only the navigation
   * entry separates that from an ordinary page view, so it is the second channel. A restored
   * document keeps its original navigation entry, so `back_forward` alone would miss real restores.
   * Adapted from upstream Horizon.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType =
      navigationEntry?.entryType === 'navigation'
        ? /** @type {PerformanceNavigationTiming} */ (navigationEntry).type
        : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // The `sectionId` getter throws without it, and this runs straight off a window listener
    if (!this.dataset.sectionId) return;

    // `cache: false`, the renderer's cache holds the same stale HTML the DOM already shows
    sectionRenderer.renderSection(this.sectionId, { cache: false }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
    });
  };

  /**
   * Holds a checkout submit until the quantity updates in flight finish.
   *
   * The cart form posts `updates[]` by position, so a row removed while its request is in
   * flight would shift every later quantity onto the wrong line.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.cart-form')) return;
    if (!CartItemsComponent.#pendingQuantityUpdates.size) return;

    event.preventDefault();
    if (this.#waitingToSubmit) return;

    this.#waitingToSubmit = true;
    const submitterName = event.submitter?.getAttribute('name');

    try {
      let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
      while (pendingQuantityUpdates.length) {
        await Promise.all(pendingQuantityUpdates);
        pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
      }
    } finally {
      this.#waitingToSubmit = false;
    }

    // The update re-renders the cart, which can replace the form and its buttons
    const currentForm = form.isConnected ? form : document.getElementById(form.id);
    if (!(currentForm instanceof HTMLFormElement)) return;

    const submitter = submitterName ? currentForm.elements.namedItem(submitterName) : null;
    currentForm.requestSubmit(
      submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement ? submitter : undefined
    );
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends. A re-render that lands first morphs the row into whatever
      // the cart now holds at its position and drops the class, so the row stays
      onAnimationEnd(row, () => {
        if (row.classList.contains('removing')) remove();
      });
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    const { line, quantity, action } = config;

    try {
      this.#disableCartItems();

      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      const fetchOptions = fetchConfig('json', { body });
      // The cart rows are pointer-events-disabled while this request is in
      // flight, so a stalled request must abort or the cart stays frozen
      if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
        fetchOptions.signal = AbortSignal.timeout(15000);
      }

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchOptions);
      const parsedResponseText = await response.json().catch(() => null);

      resetShimmer(this);

      if (parsedResponseText?.errors) {
        this.#trackCartChangeError('cart_error', {
          status: response.status,
          action,
          quantity,
          message: parsedResponseText.errors,
        });
        // A removed row is already gone from the DOM, so it has to come back from the server
        if (action === 'clear') await this.#refreshCart(line, String(parsedResponseText.errors));
        else this.#handleCartError(line, parsedResponseText);
        return;
      }

      if (!response.ok || !parsedResponseText) {
        // A rejected change answers with { status, message, description }
        const reason = parsedResponseText?.description || parsedResponseText?.message;
        this.#trackCartChangeError(parsedResponseText?.status ? 'cart_error' : response.ok ? 'parse' : 'http', {
          status: response.status,
          action,
          quantity,
          message: reason,
        });
        await this.#refreshCart(line, (response.status === 422 && reason) || CART_CHANGE_FAILED_MESSAGE);
        return;
      }

      const sectionHTML = parsedResponseText.sections?.[this.sectionId];

      if (!sectionHTML) {
        // The change went through, only its rendered section is missing
        this.#trackCartChangeError('missing_section', { status: response.status, action, quantity });
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: parsedResponseText.item_count,
            source: 'cart-items-component',
          })
        );
        await this.#refreshCart(line);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, sectionHTML);
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      this.#trackCartChangeError(error?.name === 'TimeoutError' ? 'timeout' : 'network', {
        action,
        quantity,
        message: error?.message,
      });
      await this.#refreshCart(line, CART_CHANGE_FAILED_MESSAGE);
    } finally {
      try {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  /**
   * Re-renders the cart from the server, so the rows show what the cart really holds,
   * e.g. a row removed before its request failed comes back.
   * @param {number} line - The line.
   * @param {string} [message] - The error to show on the line, when the change failed.
   */
  async #refreshCart(line, message) {
    let restored = false;

    try {
      const html = await sectionRenderer.renderSection(this.sectionId, { cache: false });
      restored = html.includes(`id="${buildSectionSelector(this.sectionId)}"`);
    } catch (error) {
      console.error(error);
    }

    if (!message) return;

    if (!restored) {
      const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);
      if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer = this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
    if (!cartItemError || !cartItemErrorContainer) return;

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * @param {string} errorType - cart_error, http, parse, missing_section, timeout or network.
   * @param {{action: string, quantity: number, status?: number, message?: unknown}} details
   */
  #trackCartChangeError(errorType, { action, quantity, status, message }) {
    window.WildYardAnalytics?.track('wild_yard_cart_change_error', {
      error_type: errorType,
      status,
      action,
      quantity,
      message: message ? String(message).slice(0, 100) : undefined,
      placement: this.closest('cart-drawer-component') ? 'drawer' : 'page',
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
